import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

interface SiteContentState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useSiteContent<T>(key: string): SiteContentState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchContent() {
      try {
        setLoading(true);
        setError(null);
        const { data: row, error: fetchError } = await supabase
          .from('vietnamtravel_site_content')
          .select('data, data_vi')
          .eq('key', key)
          .maybeSingle();

        if (fetchError) throw fetchError;
        if (!cancelled) {
          const localized = row ? loc<T>(language, row.data as T, row.data_vi) : null;
          setData(localized);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load content');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchContent();

    return () => {
      cancelled = true;
    };
  }, [key, refreshToken, language]);

  const refresh = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  return { data, loading, error, refresh };
}